import { useEffect, useRef, useState } from 'react'
import { supabase } from './supabase'

// Default-off reads preserve legacy cold boots. The database independently
// rejects old-client creation in a workshop enabled for reviewed intake.
export function validateIntakePrices(result, workshopId, module, selected) {
  if (result?.mode === 'legacy' && result.workshop_id === workshopId && result.module_key === module) return null
  const prices = result?.prices
  const tax = prices?.tax
  const ids = Array.isArray(prices?.catalogue) ? prices.catalogue.map(line => line.service_id).sort() : null
  if (result?.mode === 'enquiry' && prices?.version === 'vat-staff-enquiry-v1'
    && module === 'general' && prices.workshop_id === workshopId && prices.module_key === module
    && selected.length > 0 && JSON.stringify(ids) === JSON.stringify(selected) && prices.tax === null
    && prices.enquiry_requires_prices === true && prices.pricing_mode === 'quote_required' && prices.price_status === 'quote_required'
    && prices.tax_point_established === false && prices.document_issued === false
    && ['registered', 'not_registered'].includes(prices.configuration?.registration)) return prices
  if (result?.mode !== 'reviewed' || prices?.version !== 'vat-inherited-prices-v1'
    || prices.workshop_id !== workshopId || prices.module_key !== module
    || JSON.stringify(ids) !== JSON.stringify(selected) || !tax?.complete || tax.currency !== 'gbp'
    || !Array.isArray(tax.lines) || tax.lines.length !== selected.length
    || !['registered', 'not_registered'].includes(prices.configuration?.registration)
    || ![tax.net_minor, tax.tax_minor, tax.gross_minor].every(n => Number.isSafeInteger(n) && n >= 0)
    || tax.net_minor + tax.tax_minor !== tax.gross_minor) throw new Error('Reviewed prices are unavailable. Refresh before creating this job.')
  return prices
}

export function useVatIntakePrices(workshopId, module, serviceIds, isNew = true) {
  const enabled = isNew && process.env.REACT_APP_VAT_BUILD3 === 'true'
  const selection = JSON.stringify([...new Set(serviceIds || [])].sort())
  const identity = JSON.stringify([workshopId, module, selection])
  const [state, setState] = useState(null)
  const [refresh, setRefresh] = useState(0)
  const requestRef = useRef(null)
  useEffect(() => {
    if (!enabled) return undefined
    let current = true
    setState(null)
    const selected = JSON.parse(selection)
    Promise.resolve().then(() => supabase.rpc('workshop_vat_intake_prices', {
      p_workshop_id: workshopId, p_module: module, p_services: selected,
    })).then(({ data, error }) => {
      if (error) throw error
      const prices = validateIntakePrices(data, workshopId, module, selected)
      if (current) setState({ identity, prices, error: '' })
    }).catch(error => {
      if (current) setState({ identity, prices: null, error: error.message || 'Prices need review before this job can be created.' })
    })
    return () => { current = false }
  }, [enabled, workshopId, module, selection, identity, refresh])
  const matching = state?.identity === identity ? state : null
  const loading = enabled && !matching
  const error = enabled ? matching?.error || '' : ''
  const prices = enabled ? matching?.prices || null : null
  return { prices, loading, error, blocked: loading || Boolean(error), requestRef,
    refresh: () => { setState(null); setRefresh(n => n + 1) },
    options: { vatPrices: prices, vatRequestRef: requestRef },
    enquiry: prices?.version === 'vat-staff-enquiry-v1',
    gross: prices?.version === 'vat-staff-enquiry-v1' ? 0 : prices ? prices.tax.gross_minor / 100 : null }
}
